/**
 * Keyboard shortcuts management utility
 */

import React from "react";

export interface KeyboardShortcut {
  id: string;
  keys: string[];
  description: string;
  category: string;
  action: () => void;
  preventDefault?: boolean;
  enabled?: boolean;
}

export interface ShortcutCategory {
  name: string;
  shortcuts: KeyboardShortcut[];
}

class KeyboardShortcutsManager {
  private shortcuts: Map<string, KeyboardShortcut> = new Map();
  private listeners: Set<(event: KeyboardEvent) => void> = new Set();
  private isListening = false;

  /**
   * Register a keyboard shortcut
   */
  register(shortcut: KeyboardShortcut): void {
    const key = this.getShortcutKey(shortcut.keys);
    this.shortcuts.set(key, shortcut);

    if (!this.isListening) {
      this.startListening();
    }
  }

  /**
   * Unregister a keyboard shortcut
   */
  unregister(keys: string[]): void {
    const key = this.getShortcutKey(keys);
    this.shortcuts.delete(key);
  }

  /**
   * Get all registered shortcuts grouped by category
   */
  getShortcutsByCategory(): ShortcutCategory[] {
    const categories: Record<string, KeyboardShortcut[]> = {};

    for (const shortcut of this.shortcuts.values()) {
      if (!categories[shortcut.category]) {
        categories[shortcut.category] = [];
      }
      categories[shortcut.category].push(shortcut);
    }

    return Object.entries(categories).map(([name, shortcuts]) => ({
      name,
      shortcuts: shortcuts.sort((a, b) =>
        a.description.localeCompare(b.description),
      ),
    }));
  }

  /**
   * Format keyboard shortcut for display
   */
  formatShortcut(keys: string[]): string {
    return keys
      .map((key) => {
        switch (key.toLowerCase()) {
          case "meta":
          case "cmd":
            return "⌘";
          case "ctrl":
          case "control":
            return "⌃";
          case "alt":
          case "option":
            return "⌥";
          case "shift":
            return "⇧";
          case "enter":
            return "↵";
          case "escape":
            return "⎋";
          case "backspace":
            return "⌫";
          case "delete":
            return "⌦";
          case "tab":
            return "⇥";
          case "space":
            return "␣";
          case "arrowup":
            return "↑";
          case "arrowdown":
            return "↓";
          case "arrowleft":
            return "←";
          case "arrowright":
            return "→";
          default:
            return key.toUpperCase();
        }
      })
      .join("");
  }

  /**
   * Check if a shortcut matches the current key combination
   */
  private matchesShortcut(
    event: KeyboardEvent,
    shortcut: KeyboardShortcut,
  ): boolean {
    const eventKeys = this.getEventKeys(event);
    const shortcutKey = this.getShortcutKey(shortcut.keys);
    return eventKeys === shortcutKey;
  }

  /**
   * Get normalized key combination from event
   */
  private getEventKeys(event: KeyboardEvent): string {
    const keys: string[] = [];

    if (event.metaKey) keys.push("meta");
    if (event.ctrlKey) keys.push("ctrl");
    if (event.altKey) keys.push("alt");
    if (event.shiftKey) keys.push("shift");

    const key = event.key.toLowerCase();
    if (!["meta", "control", "alt", "shift"].includes(key)) {
      keys.push(key);
    }

    return keys.sort().join("+");
  }

  /**
   * Get normalized key combination string
   */
  private getShortcutKey(keys: string[]): string {
    return keys
      .map((key) => key.toLowerCase())
      .sort()
      .join("+");
  }

  /**
   * Start listening for keyboard events
   */
  private startListening(): void {
    if (this.isListening) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't handle shortcuts when user is typing in inputs
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      for (const shortcut of this.shortcuts.values()) {
        if (
          shortcut.enabled !== false &&
          this.matchesShortcut(event, shortcut)
        ) {
          if (shortcut.preventDefault !== false) {
            event.preventDefault();
            event.stopPropagation();
          }

          try {
            shortcut.action();
          } catch (error) {
            console.error("Error executing keyboard shortcut:", error);
          }

          break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    this.listeners.add(handleKeyDown);
    this.isListening = true;
  }

  /**
   * Stop listening for keyboard events
   */
  stopListening(): void {
    for (const listener of this.listeners) {
      document.removeEventListener("keydown", listener);
    }
    this.listeners.clear();
    this.isListening = false;
  }

  /**
   * Check if a key combination is available
   */
  isShortcutAvailable(keys: string[]): boolean {
    const key = this.getShortcutKey(keys);
    return !this.shortcuts.has(key);
  }

  /**
   * Get shortcut by key combination
   */
  getShortcut(keys: string[]): KeyboardShortcut | undefined {
    const key = this.getShortcutKey(keys);
    return this.shortcuts.get(key);
  }
}

// Global instance
export const keyboardShortcuts = new KeyboardShortcutsManager();

/**
 * Hook for managing keyboard shortcuts in React components
 */
export function useKeyboardShortcuts(shortcuts: KeyboardShortcut[]) {
  React.useEffect(() => {
    shortcuts.forEach((shortcut) => keyboardShortcuts.register(shortcut));

    return () => {
      shortcuts.forEach((shortcut) =>
        keyboardShortcuts.unregister(shortcut.keys),
      );
    };
  }, [shortcuts]);
}
